import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Eye, RefreshCw, X, Search, ArrowUpDown } from "lucide-react";

/** Admin Reports page — 12 reports, one card each, date filter + View / Excel / PDF. */
export default function AdminReports() {
  const [registry, setRegistry] = useState([]);
  const [fr, setFr] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [openKey, setOpenKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    // Mount-only registry load — api / setRegistry / toast are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    api.get("/admin/reports").then((r) => setRegistry(r.data)).catch(() => toast.error("Failed to load reports"));
  }, []);

  const visible = useMemo(() => {
    if (!q.trim()) return registry;
    const s = q.trim().toLowerCase();
    return registry.filter((r) => r.title.toLowerCase().includes(s) || r.desc.toLowerCase().includes(s));
  }, [registry, q]);

  const rangeQS = () => {
    const p = new URLSearchParams();
    if (fr) p.append("fr", fr);
    if (to) p.append("to", to);
    return p.toString();
  };

  const doView = async (key) => {
    setLoadingKey(key);
    try {
      const p = new URLSearchParams();
      if (fr) p.append("fr", fr); if (to) p.append("to", to);
      p.append("format", "json");
      const { data } = await api.get(`/admin/reports/${key}?${p}`);
      setPreview(data);
      setOpenKey(key);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally {
      setLoadingKey(null);
    }
  };

  const downloadUrl = (key, format) => {
    const p = new URLSearchParams();
    if (fr) p.append("fr", fr); if (to) p.append("to", to);
    p.append("format", format);
    const token = localStorage.getItem("samara_token") || "";
    const base = process.env.REACT_APP_BACKEND_URL;
    // Use a helper to inject auth header via fetch → blob → anchor download
    return { url: `${base}/api/admin/reports/${key}?${p}`, token, filename: `${key}.${format}` };
  };

  const download = async (key, format) => {
    setLoadingKey(`${key}:${format}`);
    try {
      const { url, token, filename } = downloadUrl(key, format);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e?.message || "Download failed");
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <DashboardLayout title="Reports">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Admin · Reports</div>
            <h1 className="font-serif text-3xl text-navy mt-1">Business Reports</h1>
            <p className="text-sm text-navy/60 mt-1">12 downloadable Excel + PDF reports with date-range filters.</p>
          </div>
          <div className="flex items-end gap-3 flex-wrap" data-testid="reports-filter-bar">
            <label className="text-xs text-navy/70">
              From
              <input type="date" value={fr} onChange={(e) => setFr(e.target.value)}
                data-testid="reports-from"
                className="ml-2 border border-line rounded-sm px-2 py-1.5 text-sm focus:border-royal outline-none" />
            </label>
            <label className="text-xs text-navy/70">
              To
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                data-testid="reports-to"
                className="ml-2 border border-line rounded-sm px-2 py-1.5 text-sm focus:border-royal outline-none" />
            </label>
            <button
              type="button"
              onClick={() => { setFr(""); setTo(""); }}
              className="text-[11px] uppercase tracking-widest text-navy/60 hover:text-navy px-2 py-1.5 border border-line rounded-sm"
              data-testid="reports-clear"
            >Clear</button>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-navy/40" />
              <input
                placeholder="Search reports…" value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="reports-search"
                className="pl-8 pr-3 py-1.5 text-sm border border-line rounded-sm focus:border-royal outline-none w-56"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
          {visible.map((r) => (
            <ReportCard
              key={r.key}
              report={r}
              busy={loadingKey?.startsWith(r.key)}
              busyKey={loadingKey}
              onView={() => doView(r.key)}
              onXlsx={() => download(r.key, "xlsx")}
              onCsv={() => download(r.key, "csv")}
              onPdf={() => download(r.key, "pdf")}
            />
          ))}
          {visible.length === 0 && (
            <div className="col-span-full text-center text-navy/50 text-sm p-10 border border-dashed border-line rounded-sm">
              No reports match &ldquo;{q}&rdquo;.
            </div>
          )}
        </div>

        {/* Preview modal */}
        {openKey && preview && (
          <PreviewModal
            report={preview}
            onClose={() => { setOpenKey(null); setPreview(null); }}
            onDownload={(fmt) => download(openKey, fmt)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function ReportCard({ report, busy, busyKey, onView, onXlsx, onCsv, onPdf }) {
  return (
    <div
      data-testid={`report-card-${report.key}`}
      className="bg-white border border-border rounded-sm p-5 hover:border-gold/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-lg text-navy leading-tight" data-testid={`report-title-${report.key}`}>{report.title}</h3>
          <p className="text-[12px] text-navy/60 mt-1 leading-snug">{report.desc}</p>
        </div>
        <ArrowUpDown size={14} className="text-gold-deep shrink-0 mt-1" />
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        <ActBtn onClick={onView} busy={busy && busyKey === report.key} icon={Eye}
                tid={`report-view-${report.key}`} label="View" />
        <ActBtn onClick={onXlsx} busy={busyKey === `${report.key}:xlsx`} icon={FileSpreadsheet}
                tid={`report-xlsx-${report.key}`} label="Excel" primary />
        <ActBtn onClick={onCsv} busy={busyKey === `${report.key}:csv`} icon={FileText}
                tid={`report-csv-${report.key}`} label="CSV" />
        <ActBtn onClick={onPdf} busy={busyKey === `${report.key}:pdf`} icon={FileText}
                tid={`report-pdf-${report.key}`} label="PDF" />
      </div>
    </div>
  );
}

function ActBtn({ onClick, busy, icon: Icon, tid, label, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={tid}
      className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2.5 py-1.5 rounded-sm disabled:opacity-50 ${
        primary ? "bg-royal text-white hover:bg-royal-deep" : "border border-line text-navy hover:bg-cream/40"
      }`}
    >
      {busy ? <RefreshCw size={11} className="animate-spin" /> : <Icon size={11} />}
      {label}
    </button>
  );
}

function PreviewModal({ report, onClose, onDownload }) {
  const rows = report.rows || [];
  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="report-preview-modal"
    >
      <div className="w-full max-w-6xl max-h-[90vh] bg-white rounded-sm border border-border shadow-2xl flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-royal">Preview · {rows.length} row{rows.length === 1 ? "" : "s"}</div>
            <h3 className="font-serif text-lg text-navy">{report.title}</h3>
            <p className="text-[11px] text-navy/50">{report.description}</p>
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => onDownload("xlsx")} className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 bg-royal text-white hover:bg-royal-deep rounded-sm" data-testid="preview-xlsx">
              <FileSpreadsheet size={12} /> Excel
            </button>
            <button onClick={() => onDownload("csv")} className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 border border-line text-navy hover:bg-cream rounded-sm" data-testid="preview-csv">
              <FileText size={12} /> CSV
            </button>
            <button onClick={() => onDownload("pdf")} className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 border border-line text-navy hover:bg-cream rounded-sm" data-testid="preview-pdf">
              <FileText size={12} /> PDF
            </button>
            <button onClick={onClose} className="text-navy/50 hover:text-navy p-1.5" data-testid="preview-close"><X size={16} /></button>
          </div>
        </div>
        <div className="overflow-auto flex-1" data-testid="report-preview-table">
          {rows.length === 0 ? (
            <div className="p-10 text-center text-navy/50 text-sm">No rows in this range.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-navy text-white sticky top-0">
                <tr>
                  {report.columns.map((c) => (
                    <th key={c.key} className="text-left px-3 py-2 font-medium uppercase tracking-widest text-[10px]">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 500).map((r, i) => (
                  <tr key={`${r[report.columns[0]?.key] ?? ""}-${i}`} className="border-t border-border/50 hover:bg-cream/20">
                    {report.columns.map((c) => (
                      <td key={c.key} className="px-3 py-1.5 text-navy whitespace-nowrap">{fmt(r[c.key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {rows.length > 500 && (
            <div className="text-center py-3 text-[11px] text-navy/50 italic">
              Preview limited to first 500 of {rows.length} rows. Download for full data.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString("en-IN");
  return String(v);
}
